import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import { fetchJudgeLanguages, toSandboxLanguages, normalizeSampleCode } from "../../../utils/judgeLanguages";

/**
 * A coding assignment being sat: its questions, the code per question and language, the
 * runner, handing each answer in, and the rules that guard it. Reloading or going back
 * is held, a tab switch is counted (a warning from the third, handed in after the
 * fifth), copy and paste in the editor are refused, and the work is handed in when the
 * time ends. Shared by StudentAssignmentSessionPage and the laptop page, so both send
 * the runner and the server exactly the same thing.
 */

/** The editor's settings, the same on both screens; a handed-in answer is read only. */
export const editorOptions = (readOnly) => ({
  fontSize: 14,
  fontFamily: "'Fira Code', 'Consolas', 'Courier New', monospace",
  minimap: { enabled: false },
  wordWrap: "on",
  scrollBeyondLastLine: false,
  padding: { top: 12 },
  formatOnType: true,
  contextmenu: false,
  lineNumbers: "on",
  renderLineHighlight: "line",
  bracketPairColorization: { enabled: true },
  readOnly,
});

const useAssignmentSession = (sessionId) => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const isDevMode = currentUser?.mode === "dev" || false;
  const editorRef = useRef(null);

  /* ── Session state ── */
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submittedQuestions, setSubmittedQuestions] = useState({});

  /* ── Per-question code & language maps ── */
  const [codeMap, setCodeMap] = useState({});
  const [langMap, setLangMap] = useState({});

  /* ── Editor / IO state ── */
  const [languages, setLanguages] = useState([]);
  const [languagesReady, setLanguagesReady] = useState(false);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);

  /* ── Bottom panel ── */
  const [activeTab, setActiveTab] = useState("input");
  const [bottomOpen, setBottomOpen] = useState(true);

  /* ── Security state ── */
  const [timeLeft, setTimeLeft] = useState(0);
  const [tabSwitches, setTabSwitches] = useState(0);

  /* ── Toast ── */
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  /* ════════════════════════════════════════════
     Data fetching
     ════════════════════════════════════════════ */

  useEffect(() => {
    fetchJudgeLanguages().then(raw => {
      setLanguages(toSandboxLanguages(raw));
      setLanguagesReady(true);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    apiClient.get(`/assignment-sessions/${sessionId}`)
      .then(res => {
        setSession(res.data.session);
        const submitted = {};
        (res.data.session.answers || []).forEach(ans => {
          const qid = ans.question._id || ans.question;
          submitted[qid] = true;
        });
        setSubmittedQuestions(submitted);
      })
      .catch(() => {
        setSession(null);
        setToast({ show: true, message: "Failed to load assignment session", type: "error" });
      })
      .finally(() => setLoading(false));
  }, [sessionId]);

  /* Initialise code/lang for each question once data is ready */
  useEffect(() => {
    if (!session || languages.length === 0) return;
    // Respect the assignment's language lock: only seed/allow the permitted
    // languages (falling back to the full list if the lock matches nothing).
    const locked = session.assignment?.lockedLanguages || [];
    const allowed = locked.length ? languages.filter(l => locked.includes(l.codeKey)) : languages;
    const pool = allowed.length ? allowed : languages;
    const initCode = {};
    const initLang = {};
    session.questions.forEach(q => {
      const normalized = normalizeSampleCode(q.sampleCode);
      const available = pool.find(l => normalized?.[l.value]);
      const langVal = available?.value || pool[0]?.value || "";
      initLang[q._id] = langVal;
      initCode[q._id] = { [langVal]: normalized?.[langVal] || "" };
    });
    setCodeMap(initCode);
    setLangMap(initLang);
  }, [session, languages]);

  /* ════════════════════════════════════════════
     Security hooks
     ════════════════════════════════════════════ */

  /* Prevent reload / close */
  useEffect(() => {
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  /* Prevent back navigation */
  useEffect(() => {
    const h = () => {
      setToast({ show: true, message: "You cannot go back during the assignment.", type: "warning" });
      window.history.pushState(null, "", window.location.href);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, []);

  /* Tab-switch tracking */
  useEffect(() => {
    const onBlur = () => { if (!isDevMode) setTabSwitches(prev => prev + 1); };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [isDevMode]);

  /* Tab-switch warnings & auto-submit */
  useEffect(() => {
    if (tabSwitches > 2 && tabSwitches < 4) {
      setToast({ show: true, message: "You switched tabs multiple times. Please focus on the assignment.", type: "warning" });
    } else if (tabSwitches === 4) {
      setToast({ show: true, message: "Last warning! Focus on the assignment or it will be auto-submitted.", type: "warning" });
    } else if (tabSwitches > 4) {
      setToast({ show: true, message: "Too many tab switches. Auto-submitting assignment.", type: "error" });
      handleAutoSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabSwitches]);

  /* Timer + auto-submit on expiry */
  useEffect(() => {
    if (!session?.startedAt || !session?.assignment?.timeLimit) return;
    const start = new Date(session.startedAt).getTime();
    const end = start + session.assignment.timeLimit * 60 * 1000;

    const tick = () => {
      const now = Date.now();
      setTimeLeft(Math.max(0, Math.floor((end - now) / 1000)));
      if (now >= end) { clearInterval(iv); handleAutoSubmit(); }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  /* ════════════════════════════════════════════
     Derived values
     ════════════════════════════════════════════ */

  const currentQ = session?.questions?.[currentIdx];
  const currentLang = currentQ ? (langMap[currentQ._id] || "") : "";
  const currentCode = currentQ ? (codeMap[currentQ._id]?.[currentLang] || "") : "";
  const isCurrentSubmitted = currentQ ? !!submittedQuestions[currentQ._id] : false;
  const testCases = currentQ
    ? (currentQ.testCases || []).filter(tc => !tc.isHidden).map(tc => ({ input: tc.input, expected: tc.expectedOutput }))
    : [];
  const passedCount = results.filter(r => r.passed).length;
  const visibleResults = results.filter(r => !r.isHidden);
  const hiddenResults = results.filter(r => r.isHidden);
  const hiddenPassed = hiddenResults.filter(r => r.passed).length;
  const normalizedCurrent = currentQ ? normalizeSampleCode(currentQ.sampleCode) : {};
  const lockedLanguages = session?.assignment?.lockedLanguages || [];
  const allowedLanguages = lockedLanguages.length
    ? languages.filter(l => lockedLanguages.includes(l.codeKey))
    : languages;
  // Guard against an empty dropdown if the lock matches none of the available
  // judge0 languages — fall back to the full list rather than trapping the student.
  const langPool = allowedLanguages.length ? allowedLanguages : languages;
  const recommendedLangs = langPool.filter(l => normalizedCurrent?.[l.value]);
  const otherLangs = langPool.filter(l => !normalizedCurrent?.[l.value]);
  const timerDanger = timeLeft > 0 && timeLeft <= 60;
  const timerWarning = timeLeft > 60 && timeLeft <= 300;

  /* ════════════════════════════════════════════
     Auto-submit (timeout / fraud)
     ════════════════════════════════════════════ */

  const handleAutoSubmit = async () => {
    if (!currentQ) return;
    const langDef = languages.find(l => l.value === currentLang);
    if (!langDef?.id) return;
    const allTcs = currentQ.testCases || [];
    const tcs = allTcs.map(tc => ({ input: tc.input, expected: tc.expectedOutput }));
    try {
      const { data } = await apiClient.post("/code/run-batch", {
        source_code: currentCode,
        language_id: langDef.id,
        testCases: tcs,
      });
      const r = (data.results || []).map((res, i) => ({ ...res, isHidden: !!allTcs[i]?.isHidden }));
      const pc = r.filter(x => x.passed).length;
      await apiClient.post(`/assignment-sessions/${sessionId}/submit`, {
        answers: [{ question: currentQ._id, code: { [currentLang]: currentCode }, results: r, passedCount: pc, total: r.length, status: pc === r.length ? "passed" : "failed", notes: pc === r.length ? "All passed" : `${r.length - pc}/${r.length} failed` }],
      });
      await apiClient.post(`/assignment-sessions/${sessionId}/submit`, { status: "submitted" });
      navigate("/s/assignments");
    } catch {
      setToast({ show: true, message: "Auto-submit failed", type: "error" });
    }
  };

  /* ════════════════════════════════════════════
     Editor actions
     ════════════════════════════════════════════ */

  const handleLangChange = (e) => {
    if (!currentQ) return;
    const v = e.target.value;
    setLangMap(prev => ({ ...prev, [currentQ._id]: v }));
    const norm = normalizeSampleCode(currentQ.sampleCode);
    if (!codeMap[currentQ._id]?.[v]) {
      setCodeMap(prev => ({ ...prev, [currentQ._id]: { ...prev[currentQ._id], [v]: norm?.[v] || "" } }));
    }
    setResults([]);
    setOutput("");
  };

  const handleCodeChange = (newCode) => {
    if (!currentQ) return;
    setCodeMap(prev => ({ ...prev, [currentQ._id]: { ...prev[currentQ._id], [currentLang]: newCode } }));
  };

  const handleRun = async () => {
    setRunning(true);
    setOutput("");
    setActiveTab("output");
    setBottomOpen(true);
    const langId = languages.find(l => l.value === currentLang)?.id;
    if (!langId) { setRunning(false); return; }
    try {
      const { data } = await apiClient.post("/code/run", { source_code: currentCode, language_id: langId, stdin: input });
      setOutput((data.stdout || data.stderr || data.compile_output || "").trim());
    } catch { setOutput("Error running code."); }
    setRunning(false);
  };

  const handleRunAll = async () => {
    if (!currentQ?.testCases?.length) return;
    setRunning(true);
    setResults([]);
    setActiveTab("results");
    setBottomOpen(true);
    const langId = languages.find(l => l.value === currentLang)?.id;
    if (!langId) { setRunning(false); return; }
    const allTcs = currentQ.testCases || [];
    const tcs = allTcs.map(tc => ({ input: tc.input, expected: tc.expectedOutput }));
    try {
      const { data } = await apiClient.post("/code/run-batch", { source_code: currentCode, language_id: langId, testCases: tcs });
      // Tag each result with hidden flag from the original test case
      const taggedResults = (data.results || []).map((r, i) => ({ ...r, isHidden: !!allTcs[i]?.isHidden }));
      setResults(taggedResults);
    } catch {
      setToast({ show: true, message: "Failed to run test cases", type: "error" });
    }
    setRunning(false);
  };

  const handleSubmit = async () => {
    if (!results.length) {
      setToast({ show: true, message: "Run all test cases first", type: "warning" });
      return;
    }
    const pc = results.filter(r => r.passed).length;
    const total = results.length;
    try {
      await apiClient.post(`/assignment-sessions/${sessionId}/submit`, {
        answers: [{ question: currentQ._id, code: { [currentLang]: currentCode }, results, passedCount: pc, total, status: pc === total ? "passed" : "failed", notes: pc === total ? "All test cases passed" : `Failed ${total - pc}/${total}` }],
      });
      setToast({ show: true, message: "Answer submitted!", type: "success" });
      setSubmittedQuestions(prev => {
        const next = { ...prev, [currentQ._id]: true };
        if (session.questions.every(q => next[q._id])) {
          apiClient.post(`/assignment-sessions/${sessionId}/submit`, { status: "submitted" })
            .then(() => setToast({ show: true, message: "Assignment submitted!", type: "success" }));
          navigate("/s/assignments");
        }
        return next;
      });
      const nextIdx = session.questions.findIndex((q, i) => !submittedQuestions[q._id] && i !== currentIdx);
      if (nextIdx !== -1) { setCurrentIdx(nextIdx); setResults([]); setOutput(""); setInput(""); }
    } catch {
      setToast({ show: true, message: "Failed to submit answer", type: "error" });
    }
  };

  const handleQuestionSwitch = (idx) => {
    setCurrentIdx(idx);
    setResults([]);
    setOutput("");
    setInput("");
  };

  /* ── Editor mount — copy/paste detection ── */
  const onEditorMount = (editor) => {
    editorRef.current = editor;
    editor.focus();
    editor.onKeyDown((event) => {
      if (isDevMode) return;
      const { keyCode, ctrlKey, metaKey } = event;
      if ((keyCode === 33 || keyCode === 52) && (metaKey || ctrlKey)) {
        event.preventDefault();
        setToast({ show: true, message: "Copy/paste is not allowed during the assignment.", type: "warning" });
      }
    });
  };

  return {
    isDevMode,
    session,
    loading,
    currentIdx,
    submittedQuestions,
    languages,
    languagesReady,
    input,
    setInput,
    output,
    results,
    running,
    activeTab,
    setActiveTab,
    bottomOpen,
    setBottomOpen,
    timeLeft,
    tabSwitches,
    toast,
    setToast,
    currentQ,
    currentLang,
    currentCode,
    isCurrentSubmitted,
    testCases,
    passedCount,
    visibleResults,
    hiddenResults,
    hiddenPassed,
    lockedLanguages,
    langPool,
    recommendedLangs,
    otherLangs,
    timerDanger,
    timerWarning,
    handleLangChange,
    handleCodeChange,
    handleRun,
    handleRunAll,
    handleSubmit,
    handleQuestionSwitch,
    onEditorMount,
  };
};

export default useAssignmentSession;
